import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin } from 'lucide-react';
import { majorAirports } from '../data/airports/majorAirports';
import { searchAirports, formatAirportLabel } from '../data/airports/utils';
import { getAirportUrl } from '../utils/navigation';
import type { Airport } from '../data/airports/types';

interface AirportSearchInputProps {
  selectedAirport?: string;
  onSelect: (airportId: string) => void;
}

export function AirportSearchInput({ selectedAirport, onSelect }: AirportSearchInputProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [airports, setAirports] = useState(majorAirports);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (query.length >= 2 && !isLoading) {
      setIsLoading(true);
      import('../data/airports/fullAirportList').then(({ airports }) => {
        setAirports(airports);
        setIsLoading(false);
      });
    }
  }, [query]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedAirportData = airports.find(a => a.id === selectedAirport);

  const handleAirportSelect = (airport: Airport) => {
    setIsOpen(false);
    setQuery('');
    onSelect(airport.id);
    navigate(getAirportUrl(airport));
  };

  const filteredAirports = searchAirports(airports, query);

  return (
    <div className="relative flex-1 min-w-[200px] sm:min-w-[240px]" ref={dropdownRef}>
      <div className="relative group">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedAirportData ? formatAirportLabel(selectedAirportData) : "Search airports..."}
          className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 bg-white/90 backdrop-blur-sm border-0 rounded-xl sm:rounded-2xl text-sm sm:text-base text-gray-900 placeholder-gray-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-all duration-200 shadow-lg"
        />
        <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2">
          <Search className="text-gray-400 group-focus-within:text-primary-500 transition-colors duration-200" size={16} />
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-[100] w-full mt-2 bg-white/95 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-2xl border border-gray-200/50 max-h-80 sm:max-h-96 overflow-hidden">
          <div className="overflow-auto max-h-80 sm:max-h-96">
            {isLoading ? (
              <div className="p-4 sm:p-6 text-center">
                <div className="inline-flex items-center gap-2 text-gray-500">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm">Loading airports...</span>
                </div>
              </div>
            ) : filteredAirports.length === 0 ? (
              <div className="p-4 sm:p-6 text-center text-gray-500">
                <MapPin className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-300" />
                <span className="text-sm">No airports found</span>
              </div>
            ) : (
              filteredAirports.map((airport) => (
                <button
                  key={airport.id}
                  onClick={() => handleAirportSelect(airport)}
                  className="w-full px-4 py-3 sm:px-6 sm:py-4 text-left hover:bg-primary-50 focus:bg-primary-50 transition-colors duration-150 border-b border-gray-100 last:border-b-0 group"
                >
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="p-1.5 sm:p-2 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors duration-150">
                      <MapPin size={12} className="text-primary-600 sm:w-4 sm:h-4" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 group-hover:text-primary-700 transition-colors duration-150 text-sm sm:text-base">
                        {airport.iata} - {airport.city}
                      </div>
                      <div className="text-xs sm:text-sm text-gray-500 group-hover:text-gray-600 transition-colors duration-150 line-clamp-1">
                        {airport.name}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}